import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import type { K8SSnapshot, K8SSnapshotNode } from "../hooks";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export type SnapshotLike = Pick<
  K8SSnapshot,
  | "id"
  | "name"
  | "project"
  | "job_ref"
  | "job_sha"
  | "job_url"
  | "job_id"
  | "job_name"
  | "created_at"
> &
  Partial<K8SSnapshot | K8SSnapshotNode>;

const SHORT_SHA_LEN = 7;
const SHORT_ID_LEN = 8;

export function getSnapshotProjectLeaf(s: SnapshotLike): string | undefined {
  const project = s.project?.trim();
  if (!project) return undefined;
  const parts = project.split("/").filter(Boolean);
  return parts[parts.length - 1] || undefined;
}

export function getSnapshotProjectPath(s: SnapshotLike): string | undefined {
  return s.project?.trim() || undefined;
}

/** Parse job id: prefer explicit `job_id`, else trailing int from `job_url`. */
export function getSnapshotJobId(s: SnapshotLike): string | undefined {
  const explicit = s.job_id?.trim();
  if (explicit) return explicit;
  const url = s.job_url?.trim();
  if (!url) return undefined;
  const match = url.match(/\/jobs\/(\d+)(?:[/?#]|$)/);
  return match?.[1];
}

export function getSnapshotJobName(s: SnapshotLike): string | undefined {
  return s.job_name?.trim() || undefined;
}

export function getSnapshotShortSha(s: SnapshotLike): string | undefined {
  const sha = s.job_sha?.trim();
  if (!sha) return undefined;
  return sha.slice(0, SHORT_SHA_LEN);
}

export function getSnapshotFullSha(s: SnapshotLike): string | undefined {
  return s.job_sha?.trim() || undefined;
}

export function getSnapshotShortId(s: SnapshotLike): string | undefined {
  const id = s.id?.trim();
  if (!id) return undefined;
  return id.slice(0, SHORT_ID_LEN);
}

export function getSnapshotRef(s: SnapshotLike): string | undefined {
  return s.job_ref?.trim() || undefined;
}

/**
 * Short, human form of a git ref: `refs/merge-requests/125/head` → `!125`
 * (GitLab's MR notation), `refs/heads/main` → `main`, `refs/tags/v1` → `v1`.
 */
export function shortRef(ref: string): string {
  const mr = ref.match(/^refs\/merge-requests\/(\d+)\/(?:head|merge)$/);
  if (mr) return `!${mr[1]}`;
  return ref.replace(/^refs\/(?:heads|tags)\//, "");
}

/**
 * Primary derived label:
 *   {projectLeaf}[ · {ref}] · {jobName | #jobId | @shortId}
 * Prefers the CI job name; falls back to `#jobId`, then to `@shortId` so
 * items are never visually identical. Ref is omitted silently when absent.
 */
export function getSnapshotDisplayName(s: SnapshotLike): string {
  const leaf = getSnapshotProjectLeaf(s);
  const ref = getSnapshotRef(s);
  const jobName = getSnapshotJobName(s);
  const jobId = getSnapshotJobId(s);
  const shortId = getSnapshotShortId(s);

  const parts: string[] = [];
  if (leaf) parts.push(leaf);
  if (ref) parts.push(shortRef(ref));

  if (jobName) {
    parts.push(jobName);
  } else if (jobId) {
    parts.push(`#${jobId}`);
  } else if (shortId) {
    parts.push(`@${shortId}`);
  }

  if (parts.length === 0) {
    return s.name ?? "snapshot";
  }
  return parts.join(" · ");
}

export function getSnapshotRelativeTime(s: SnapshotLike): string | undefined {
  const t = s.created_at;
  if (!t) return undefined;
  const d = dayjs(t);
  if (!d.isValid()) return undefined;
  return d.fromNow();
}

export function getSnapshotAbsoluteTime(s: SnapshotLike): string | undefined {
  const t = s.created_at;
  if (!t) return undefined;
  const d = dayjs.utc(t);
  if (!d.isValid()) return undefined;
  return `${d.format("YYYY-MM-DD HH:mm:ss")} UTC`;
}

/** Secondary line: `{shortSha} · {relativeTime}`. Omits missing parts. */
export function getSnapshotSecondary(s: SnapshotLike): string | undefined {
  const parts: string[] = [];
  const sha = getSnapshotShortSha(s);
  if (sha) parts.push(sha);
  const rel = getSnapshotRelativeTime(s);
  if (rel) parts.push(rel);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * Single lowercased haystack for table search/filter. Combines raw name,
 * derived label, project path, ref, SHA, job id, and job name.
 */
export function getSnapshotFilterHaystack(s: SnapshotLike): string {
  return [
    s.name,
    getSnapshotDisplayName(s),
    getSnapshotProjectPath(s),
    getSnapshotProjectLeaf(s),
    getSnapshotRef(s),
    getSnapshotFullSha(s),
    getSnapshotShortSha(s),
    getSnapshotJobId(s),
    getSnapshotJobName(s),
    s.id,
  ]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join(" ")
    .toLowerCase();
}

/**
 * Sort comparator by derived label: project → ref → job name (alpha) →
 * numeric job id (desc) → raw name.
 */
export function compareSnapshotsByDisplayName(
  a: SnapshotLike,
  b: SnapshotLike,
): number {
  const la = getSnapshotProjectLeaf(a) ?? "";
  const lb = getSnapshotProjectLeaf(b) ?? "";
  if (la !== lb) return la.localeCompare(lb);

  const ra = getSnapshotRef(a) ?? "";
  const rb = getSnapshotRef(b) ?? "";
  if (ra !== rb) return ra.localeCompare(rb);

  const na = getSnapshotJobName(a) ?? "";
  const nb = getSnapshotJobName(b) ?? "";
  if (na !== nb) return na.localeCompare(nb);

  const ja = Number(getSnapshotJobId(a) ?? NaN);
  const jb = Number(getSnapshotJobId(b) ?? NaN);
  const jaOk = Number.isFinite(ja);
  const jbOk = Number.isFinite(jb);
  if (jaOk && jbOk && ja !== jb) return jb - ja;
  if (jaOk !== jbOk) return jaOk ? -1 : 1;

  return (a.name ?? "").localeCompare(b.name ?? "");
}

/**
 * Middle-ellipsis truncation for long technical identifiers.
 * Preserves the leading and trailing characters, hides the middle.
 */
export function truncateMiddle(value: string, maxLen = 40): string {
  if (value.length <= maxLen) return value;
  const keep = maxLen - 1;
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}
